/**
 * Design Workflow — shared contract (API <-> web <-> worker).
 *
 * Business process (Cloudnine "Workflow Final.xlsx", V2): Business Requester
 * raises a request -> Regional Coordinator reviews (and owns the ETA) ->
 * Content Team reviews/corrects -> Regional Coordinator approves the content
 * -> Regional Coordinator assigns a Design Team Lead -> the lead assigns a
 * Designer (or themselves) -> designer submits versioned designs (up to 5
 * files each) -> lead reviews (a different lead when the lead designed it) ->
 * Regional Coordinator reviews the design -> Content Team final QC ->
 * Regional Coordinator shares with Business for FEEDBACK (checkpoint 1) ->
 * Final Approvers decide under the configured policy -> Regional Coordinator
 * hands the approved files to Business for RELEASE CONFIRMATION (checkpoint
 * 2) -> Regional Coordinator releases. Every correction (QC, Business
 * feedback, final rejection, release confirmation) returns to the Regional
 * Coordinator, who routes it to Content and/or Design on the SAME request.
 *
 * WHO may act is never decided by a name in code. It is decided server-side
 * from (a) configuration rows (`design_workflow_regions`,
 * `design_workflow_members`) and (b) the request's own assignments. The web
 * uses `availableActions` from the API response only to decide which buttons
 * to render; the API re-checks every action (`apps/api/src/modules/design-workflow`).
 */
import { z } from 'zod';
// ---------------------------------------------------------------------------
// Vocabulary
// ---------------------------------------------------------------------------
export const DESIGN_REQUEST_STATUSES = [
    'DRAFT',
    'REGIONAL_REVIEW',
    'CONTENT_REVIEW',
    'REGIONAL_CONTENT_REVIEW',
    'READY_FOR_DESIGN_ASSIGNMENT',
    'DESIGN_ASSIGNED',
    'DESIGN_IN_PROGRESS',
    'DESIGN_LEAD_REVIEW',
    'REGIONAL_DESIGN_REVIEW',
    'FINAL_CONTENT_REVIEW',
    'CHANGE_ROUTING',
    'READY_FOR_BUSINESS_FEEDBACK',
    'BUSINESS_FEEDBACK',
    'FINAL_APPROVAL',
    'READY_FOR_BUSINESS_REVIEW',
    'BUSINESS_REVIEW',
    'CHANGES_REQUESTED',
    'CONFIRMED_FOR_RELEASE',
    'RELEASED',
    'CANCELLED',
];
export const DESIGN_REQUEST_STATUS_LABELS = {
    DRAFT: 'Draft',
    REGIONAL_REVIEW: 'Regional review',
    CONTENT_REVIEW: 'Content review',
    REGIONAL_CONTENT_REVIEW: 'Coordinator content review',
    READY_FOR_DESIGN_ASSIGNMENT: 'Awaiting design assignment',
    DESIGN_ASSIGNED: 'Awaiting designer',
    DESIGN_IN_PROGRESS: 'Design in progress',
    DESIGN_LEAD_REVIEW: 'Design lead review',
    REGIONAL_DESIGN_REVIEW: 'Coordinator design review',
    FINAL_CONTENT_REVIEW: 'Final content review',
    CHANGE_ROUTING: 'Changes to route',
    READY_FOR_BUSINESS_FEEDBACK: 'Ready to share for feedback',
    BUSINESS_FEEDBACK: 'Business feedback',
    FINAL_APPROVAL: 'Final approval',
    READY_FOR_BUSINESS_REVIEW: 'Ready to share for release',
    BUSINESS_REVIEW: 'Business release confirmation',
    CHANGES_REQUESTED: 'Changes requested',
    CONFIRMED_FOR_RELEASE: 'Confirmed for release',
    RELEASED: 'Released',
    CANCELLED: 'Cancelled',
};
/** Terminal statuses: nothing changes a request in these. */
export const DESIGN_TERMINAL_STATUSES = ['RELEASED', 'CANCELLED'];
/** Statuses counted as "open" (submitted and not finished). */
export const DESIGN_OPEN_STATUSES = DESIGN_REQUEST_STATUSES.filter((s) => s !== 'DRAFT' && s !== 'RELEASED' && s !== 'CANCELLED');
/**
 * The progress stepper (web) — the main path in order. Each status maps to
 * the step it belongs to; CHANGES_REQUESTED shows as the step it returned
 * from (`changesReturnStatus`), CANCELLED shows where it stopped.
 */
export const DESIGN_PROGRESS_STEPS = [
    { key: 'REQUEST', label: 'Request' },
    { key: 'REGIONAL', label: 'Regional review' },
    { key: 'CONTENT', label: 'Content review' },
    { key: 'REGIONAL_CONTENT', label: 'Coordinator content check' },
    { key: 'ASSIGNMENT', label: 'Design assignment' },
    { key: 'DESIGN', label: 'Design' },
    { key: 'LEAD_REVIEW', label: 'Lead review' },
    { key: 'REGIONAL_DESIGN', label: 'Coordinator design check' },
    { key: 'FINAL_CONTENT', label: 'Final content QC' },
    { key: 'FEEDBACK', label: 'Business feedback' },
    { key: 'APPROVAL', label: 'Final approval' },
    { key: 'BUSINESS', label: 'Release confirmation' },
    { key: 'RELEASE', label: 'Release' },
];
export const DESIGN_STATUS_STEP = {
    DRAFT: 'REQUEST',
    CHANGES_REQUESTED: 'REQUEST',
    REGIONAL_REVIEW: 'REGIONAL',
    CONTENT_REVIEW: 'CONTENT',
    REGIONAL_CONTENT_REVIEW: 'REGIONAL_CONTENT',
    READY_FOR_DESIGN_ASSIGNMENT: 'ASSIGNMENT',
    DESIGN_ASSIGNED: 'ASSIGNMENT',
    DESIGN_IN_PROGRESS: 'DESIGN',
    DESIGN_LEAD_REVIEW: 'LEAD_REVIEW',
    REGIONAL_DESIGN_REVIEW: 'REGIONAL_DESIGN',
    FINAL_CONTENT_REVIEW: 'FINAL_CONTENT',
    // The step whose correction is being routed (resolved from `reworkOrigin` by the web; this is the fallback).
    CHANGE_ROUTING: 'FINAL_CONTENT',
    READY_FOR_BUSINESS_FEEDBACK: 'FEEDBACK',
    BUSINESS_FEEDBACK: 'FEEDBACK',
    FINAL_APPROVAL: 'APPROVAL',
    READY_FOR_BUSINESS_REVIEW: 'BUSINESS',
    BUSINESS_REVIEW: 'BUSINESS',
    CONFIRMED_FOR_RELEASE: 'RELEASE',
    RELEASED: 'RELEASE',
    CANCELLED: 'REQUEST',
};
export const DESIGN_PRIORITIES = ['LOW', 'MEDIUM', 'HIGH', 'URGENT'];
export const DESIGN_PRIORITY_LABELS = { LOW: 'Low', MEDIUM: 'Medium', HIGH: 'High', URGENT: 'Urgent' };
export const DESIGN_REQUEST_TYPES = [
    'SOCIAL_MEDIA_POST',
    'POSTER',
    'BANNER',
    'BROCHURE',
    'FLYER',
    'EMAILER',
    'STANDEE',
    'PRINT_AD',
    'VIDEO',
    'OTHER',
];
export const DESIGN_REQUEST_TYPE_LABELS = {
    SOCIAL_MEDIA_POST: 'Social media post',
    POSTER: 'Poster',
    BANNER: 'Banner',
    BROCHURE: 'Brochure',
    FLYER: 'Flyer',
    EMAILER: 'Emailer',
    STANDEE: 'Standee',
    PRINT_AD: 'Print advertisement',
    VIDEO: 'Video',
    OTHER: 'Other',
};
/** Functional roles (configuration rows, not RBAC roles). */
export const DESIGN_WORKFLOW_ROLES = [
    'BUSINESS_REQUESTER',
    'CONTENT_REVIEWER',
    'WORKFLOW_COORDINATOR',
    'DESIGN_LEAD',
    'DESIGNER',
    'FINAL_APPROVER',
    'REQUEST_CC',
];
export const DESIGN_WORKFLOW_ROLE_LABELS = {
    BUSINESS_REQUESTER: 'Business requester',
    CONTENT_REVIEWER: 'Content reviewer',
    WORKFLOW_COORDINATOR: 'Workflow coordinator',
    DESIGN_LEAD: 'Design team lead',
    DESIGNER: 'Designer',
    FINAL_APPROVER: 'Final approver',
    REQUEST_CC: 'CC on new requests',
};
/** Which checkpoint sent the request to CHANGE_ROUTING (decides the way back after the correction). */
export const DESIGN_REWORK_ORIGINS = ['FINAL_CONTENT_REVIEW', 'BUSINESS_FEEDBACK', 'FINAL_APPROVAL', 'BUSINESS_REVIEW'];
export const DESIGN_REWORK_ORIGIN_LABELS = {
    FINAL_CONTENT_REVIEW: 'Final content QC',
    BUSINESS_FEEDBACK: 'Business feedback',
    FINAL_APPROVAL: 'Final approval',
    BUSINESS_REVIEW: 'Business release confirmation',
};
/** CHANGE_ROUTING status for each origin (the step the progress bar shows while the coordinator routes it). */
export const DESIGN_REWORK_ORIGIN_STEP = {
    FINAL_CONTENT_REVIEW: 'FINAL_CONTENT',
    BUSINESS_FEEDBACK: 'FEEDBACK',
    FINAL_APPROVAL: 'APPROVAL',
    BUSINESS_REVIEW: 'BUSINESS',
};
/** What a change request affects (business / final-approver / final-content loops). */
export const DESIGN_CHANGE_AREAS = ['CONTENT', 'DESIGN', 'BOTH'];
export const DESIGN_CHANGE_AREA_LABELS = { CONTENT: 'Content', DESIGN: 'Design', BOTH: 'Content and design' };
export const DESIGN_APPROVAL_STATUSES = ['PENDING', 'APPROVED', 'REJECTED', 'SUPERSEDED'];
/**
 * How the final-approval round is decided (Admin -> Settings -> Design
 * Workflow, per organisation). The policy in force is SNAPSHOTTED onto the
 * request when a round opens, so changing the setting never alters an open
 * round or a past result. The evaluator below is the single place a policy
 * is interpreted. SEQUENTIAL is deliberately not offered.
 *  - ALL_REQUIRED: every REQUIRED approver of the round must approve.
 *  - ANY_ONE: one approval from any approver of the round completes it.
 * Under both, the first REJECT closes the round (it goes back for changes).
 */
export const DESIGN_APPROVAL_POLICIES = ['ALL_REQUIRED', 'ANY_ONE'];
export const DEFAULT_DESIGN_APPROVAL_POLICY = 'ALL_REQUIRED';
export const DESIGN_APPROVAL_POLICY_LABELS = {
    ALL_REQUIRED: 'Every required approver must approve',
    ANY_ONE: 'Any one approver is enough',
};
export const DESIGN_ATTACHMENT_CATEGORIES = ['REFERENCE', 'CONTENT', 'DESIGN'];
/** Categories a user may upload through the general attachments endpoint (DESIGN files come in through design versions only). */
export const DESIGN_UPLOADABLE_CATEGORIES = ['REFERENCE', 'CONTENT'];
/** Reference / content attachments: same file policy as the rest of the product (5 MiB each, server-sniffed PDF/JPEG/PNG/WEBP). */
export const MAX_DESIGN_ATTACHMENT_BYTES = 5 * 1024 * 1024;
export const MAX_DESIGN_ATTACHMENTS = 50;
/** Designer creative files (a design version): up to 5 files of up to 50 MiB, uploaded straight to blob storage. */
export const MAX_DESIGN_FILE_BYTES = 50 * 1024 * 1024;
export const MAX_DESIGN_FILES_PER_VERSION = 5;
/** Lifetime of a direct-upload token (intent -> upload -> version). */
export const DESIGN_UPLOAD_TOKEN_TTL_SECONDS = 2 * 60 * 60;
export const DESIGN_ALLOWED_MIME_TYPES = ['application/pdf', 'image/jpeg', 'image/png', 'image/webp'];
export const DESIGN_LIMITS = {
    title: 200,
    description: 10000,
    content: 20000,
    notes: 5000,
    instructions: 5000,
    reason: 2000,
    comment: 5000,
    link: 2000,
    links: 20,
    regionName: 100,
};
// ---------------------------------------------------------------------------
// Actions (explicit, protected workflow operations)
// ---------------------------------------------------------------------------
export const DESIGN_ACTIONS = [
    'EDIT',
    'SUBMIT',
    'REGIONAL_APPROVE',
    'REGIONAL_REQUEST_CHANGES',
    'CONTENT_REVISE',
    'CONTENT_APPROVE',
    'CONTENT_REQUEST_CLARIFICATION',
    'REGIONAL_CONTENT_APPROVE',
    'REGIONAL_CONTENT_RETURN',
    'ASSIGN_DESIGN_LEAD',
    'ASSIGN_DESIGNER',
    'UPLOAD_DESIGN',
    'SUBMIT_DESIGN',
    'DESIGN_APPROVE',
    'DESIGN_REQUEST_CHANGES',
    'REGIONAL_DESIGN_FORWARD',
    'REGIONAL_DESIGN_RETURN',
    'FINAL_CONTENT_APPROVE',
    'FINAL_CONTENT_REQUEST_CHANGES',
    'ROUTE_CHANGES',
    'SHARE_FOR_BUSINESS_FEEDBACK',
    'BUSINESS_ACCEPT_FOR_APPROVAL',
    'BUSINESS_FEEDBACK_CHANGES',
    'FINAL_APPROVE',
    'FINAL_REJECT',
    'SEND_TO_BUSINESS',
    'BUSINESS_CONFIRM',
    'BUSINESS_REQUEST_CHANGES',
    'RELEASE',
    'CANCEL',
    'SET_ETA',
    'COMMENT',
    'ATTACH',
];
export const DESIGN_ACTION_LABELS = {
    EDIT: 'Edit request',
    SUBMIT: 'Submit request',
    REGIONAL_APPROVE: 'Approve & forward',
    REGIONAL_REQUEST_CHANGES: 'Request changes',
    CONTENT_REVISE: 'Add revised content',
    CONTENT_APPROVE: 'Approve content',
    CONTENT_REQUEST_CLARIFICATION: 'Request clarification',
    REGIONAL_CONTENT_APPROVE: 'Approve content for design',
    REGIONAL_CONTENT_RETURN: 'Return to content team',
    ASSIGN_DESIGN_LEAD: 'Assign design lead',
    ASSIGN_DESIGNER: 'Assign designer',
    UPLOAD_DESIGN: 'Upload design version',
    SUBMIT_DESIGN: 'Submit design for review',
    DESIGN_APPROVE: 'Approve design',
    DESIGN_REQUEST_CHANGES: 'Request design changes',
    REGIONAL_DESIGN_FORWARD: 'Forward to content QC',
    REGIONAL_DESIGN_RETURN: 'Return to designer',
    FINAL_CONTENT_APPROVE: 'Approve final content',
    FINAL_CONTENT_REQUEST_CHANGES: 'Report a correction',
    ROUTE_CHANGES: 'Route the changes',
    SHARE_FOR_BUSINESS_FEEDBACK: 'Share with business for feedback',
    BUSINESS_ACCEPT_FOR_APPROVAL: 'Accept for final approval',
    BUSINESS_FEEDBACK_CHANGES: 'Request changes',
    FINAL_APPROVE: 'Approve',
    FINAL_REJECT: 'Reject',
    SEND_TO_BUSINESS: 'Share for release confirmation',
    BUSINESS_CONFIRM: 'Confirm for release',
    BUSINESS_REQUEST_CHANGES: 'Request further changes',
    RELEASE: 'Release',
    CANCEL: 'Cancel request',
    SET_ETA: 'Set ETA',
    COMMENT: 'Comment',
    ATTACH: 'Attach file',
};
// ---------------------------------------------------------------------------
// Request DTOs
// ---------------------------------------------------------------------------
const optionalText = (max) => z
    .string()
    .trim()
    .max(max)
    .transform((v) => (v.length === 0 ? null : v))
    .nullable();
const requiredReason = () => z.string().trim().min(1, 'A reason is required').max(DESIGN_LIMITS.reason);
const optionalComment = () => optionalText(DESIGN_LIMITS.reason).optional();
/** Calendar date `YYYY-MM-DD` (due dates are dates, not instants). "" / null clears. */
const dateOnly = () => z.union([
    z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD')
        .refine((v) => !Number.isNaN(Date.parse(`${v}T00:00:00Z`)), 'Invalid date'),
    z.literal('').transform(() => null),
    z.null(),
]);
const link = z
    .string()
    .trim()
    .max(DESIGN_LIMITS.link)
    .refine((v) => {
    try {
        const u = new URL(v);
        return u.protocol === 'https:' || u.protocol === 'http:';
    }
    catch {
        return false;
    }
}, 'Each reference link must be an http(s) URL');
const designRequestFieldShape = {
    title: z.string().trim().min(1).max(DESIGN_LIMITS.title),
    requestType: z.enum(DESIGN_REQUEST_TYPES),
    regionId: z.string().uuid(),
    description: optionalText(DESIGN_LIMITS.description),
    content: optionalText(DESIGN_LIMITS.content),
    priority: z.enum(DESIGN_PRIORITIES),
    dueDate: dateOnly(),
    referenceLinks: z
        .array(link)
        .max(DESIGN_LIMITS.links)
        .transform((a) => [...new Set(a)]),
    notes: optionalText(DESIGN_LIMITS.notes),
};
/** `POST /design-requests` — creates a DRAFT. Title, type and region are needed even for a draft. */
export const createDesignRequestDtoSchema = z
    .object(designRequestFieldShape)
    .partial({ description: true, content: true, priority: true, dueDate: true, referenceLinks: true, notes: true })
    .strict();
/** `PATCH /design-requests/:id` — DRAFT or CHANGES_REQUESTED, requester only. Omitted = unchanged. */
export const updateDesignRequestDtoSchema = z
    .object(designRequestFieldShape)
    .partial()
    .strict()
    .refine((v) => Object.keys(v).length > 0, { message: 'at least one field is required' });
export const DESIGN_LIST_VIEWS = ['all', 'mine', 'action', 'approval', 'completed', 'overdue'];
export const DESIGN_LIST_VIEW_LABELS = {
    all: 'All requests',
    mine: 'My requests',
    action: 'Assigned to me',
    approval: 'Awaiting my approval',
    completed: 'Completed / released',
    overdue: 'Overdue',
};
/** `dueDate` sorts by the effective due date (the coordinator's ETA when set, else the requested date). */
export const DESIGN_SORT_FIELDS = ['updatedAt', 'createdAt', 'dueDate', 'number'];
/** `GET /design-requests` */
export const designRequestListQueryDtoSchema = z
    .object({
    view: z.enum(DESIGN_LIST_VIEWS).default('all'),
    q: z.string().trim().max(200).optional(),
    status: z.enum(DESIGN_REQUEST_STATUSES).optional(),
    regionId: z.string().uuid().optional(),
    priority: z.enum(DESIGN_PRIORITIES).optional(),
    requesterId: z.string().uuid().optional(),
    designerId: z.string().uuid().optional(),
    designLeadId: z.string().uuid().optional(),
    sort: z.enum(DESIGN_SORT_FIELDS).default('updatedAt'),
    order: z.enum(['asc', 'desc']).default('desc'),
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(100).default(25),
})
    .strict();
// Action bodies -------------------------------------------------------------
export const designReasonDtoSchema = z.object({ reason: requiredReason() }).strict();
export const designOptionalCommentDtoSchema = z.object({ comment: optionalComment() }).strict();
export const designContentRevisionDtoSchema = z
    .object({
    content: z.string().trim().min(1).max(DESIGN_LIMITS.content),
    note: optionalText(DESIGN_LIMITS.reason).optional(),
})
    .strict();
export const designAssignLeadDtoSchema = z
    .object({ designLeadId: z.string().uuid(), note: optionalText(DESIGN_LIMITS.reason).optional() })
    .strict();
export const designAssignDesignerDtoSchema = z
    .object({
    designerId: z.string().uuid(),
    dueDate: dateOnly().optional(),
    instructions: optionalText(DESIGN_LIMITS.instructions).optional(),
})
    .strict();
/**
 * Final content QC, Business feedback / release confirmation reporting a problem. It goes to the
 * Regional Coordinator (CHANGE_ROUTING), who decides where it is fixed. `area` is the reporter's
 * SUGGESTION only (optional): "wrong text in the design" = DESIGN, "the approved copy must change" = CONTENT.
 */
export const designChangeRequestDtoSchema = z
    .object({ area: z.enum(DESIGN_CHANGE_AREAS).optional(), reason: requiredReason() })
    .strict();
/** Kept as an alias: final content QC uses the same body. */
export const designFinalContentChangesDtoSchema = designChangeRequestDtoSchema;
/** Final approver rejection: a reason; the coordinator routes the correction. */
export const designFinalRejectDtoSchema = z.object({ reason: requiredReason(), area: z.enum(DESIGN_CHANGE_AREAS).optional() }).strict();
/** `POST :id/route-changes` — the Regional Coordinator decides where a correction is made. */
export const designRouteChangesDtoSchema = z
    .object({ area: z.enum(DESIGN_CHANGE_AREAS), instructions: z.string().trim().min(1, 'Instructions are required').max(DESIGN_LIMITS.instructions) })
    .strict();
/** `POST :id/eta` — Regional Coordinator sets or changes the ETA (shown at the top as the due date). null clears it. */
export const designSetEtaDtoSchema = z.object({ etaDate: dateOnly(), reason: optionalComment() }).strict();
/** `POST :id/design-uploads` — ask to upload ONE design file straight to blob storage. */
export const designUploadIntentDtoSchema = z
    .object({
    fileName: z.string().trim().min(1).max(255),
    contentType: z.enum(DESIGN_ALLOWED_MIME_TYPES),
    sizeBytes: z.number().int().positive().max(MAX_DESIGN_FILE_BYTES),
})
    .strict();
/** `POST :id/design-versions/from-uploads` — one design version made of 1..5 uploaded files. */
export const designVersionFromUploadsDtoSchema = z
    .object({
    note: optionalText(DESIGN_LIMITS.reason).optional(),
    uploadTokens: z
        .array(z.string().min(1).max(4096))
        .min(1, 'Add at least one file')
        .max(MAX_DESIGN_FILES_PER_VERSION, `At most ${MAX_DESIGN_FILES_PER_VERSION} files per version`)
        .refine((a) => new Set(a).size === a.length, 'Each file can be used once'),
})
    .strict();
/** `PATCH /admin/design-workflow/settings` */
export const designWorkflowSettingsUpdateDtoSchema = z.object({ finalApprovalPolicy: z.enum(DESIGN_APPROVAL_POLICIES) }).strict();
/** `PATCH /admin/design-workflow/members/:id` — only a final approver's "required" flag is editable. */
export const designMemberUpdateDtoSchema = z.object({ isRequired: z.boolean() }).strict();
export const designCommentDtoSchema = z.object({ body: z.string().trim().min(1).max(DESIGN_LIMITS.comment) }).strict();
// Configuration (Admin -> Settings -> Design Workflow) -----------------------
export const designRegionCreateDtoSchema = z
    .object({
    name: z.string().trim().min(1).max(DESIGN_LIMITS.regionName),
    coordinatorUserId: z.string().uuid(),
})
    .strict();
export const designRegionUpdateDtoSchema = z
    .object({
    name: z.string().trim().min(1).max(DESIGN_LIMITS.regionName),
    coordinatorUserId: z.string().uuid(),
    isActive: z.boolean(),
})
    .partial()
    .strict()
    .refine((v) => Object.keys(v).length > 0, { message: 'at least one field is required' });
export const designMemberCreateDtoSchema = z
    .object({
    role: z.enum(DESIGN_WORKFLOW_ROLES),
    userId: z.string().uuid(),
    /** Required for DESIGNER (the design lead they report to); must be absent otherwise. */
    leadUserId: z.string().uuid().nullable().optional(),
    /** FINAL_APPROVER only: a required approver must approve before the request proceeds. Defaults to true. */
    isRequired: z.boolean().optional(),
    /** REQUEST_CC only: CC just for requests in this region (absent/null = every region). */
    regionId: z.string().uuid().nullable().optional(),
    /** REQUEST_CC only: CC just for requests raised by this person (absent/null = any requester). */
    requesterUserId: z.string().uuid().nullable().optional(),
})
    .strict()
    .superRefine((v, ctx) => {
    if (v.role === 'DESIGNER' && !v.leadUserId)
        ctx.addIssue({ code: 'custom', path: ['leadUserId'], message: 'A designer needs a design lead' });
    if (v.role !== 'DESIGNER' && v.leadUserId)
        ctx.addIssue({ code: 'custom', path: ['leadUserId'], message: 'Only a designer has a design lead' });
    if (v.leadUserId && v.leadUserId === v.userId)
        ctx.addIssue({ code: 'custom', path: ['leadUserId'], message: 'A designer cannot be their own lead' });
    if (v.role !== 'REQUEST_CC' && (v.regionId || v.requesterUserId))
        ctx.addIssue({ code: 'custom', path: ['regionId'], message: 'Only a CC entry can be limited to a region or requester' });
});
/** `POST /admin/design-workflow/members/bulk` — add several people to one role at once (the "+" in the add dialog). */
export const designMembersBulkCreateDtoSchema = z
    .object({
    role: z.enum(DESIGN_WORKFLOW_ROLES),
    userIds: z
        .array(z.string().uuid())
        .min(1)
        .max(50)
        .transform((a) => [...new Set(a)]),
    leadUserId: z.string().uuid().nullable().optional(),
    isRequired: z.boolean().optional(),
    regionId: z.string().uuid().nullable().optional(),
    requesterUserId: z.string().uuid().nullable().optional(),
})
    .strict()
    .superRefine((v, ctx) => {
    if (v.role === 'DESIGNER' && !v.leadUserId)
        ctx.addIssue({ code: 'custom', path: ['leadUserId'], message: 'A designer needs a design lead' });
    if (v.role !== 'DESIGNER' && v.leadUserId)
        ctx.addIssue({ code: 'custom', path: ['leadUserId'], message: 'Only a designer has a design lead' });
    if (v.leadUserId && v.userIds.includes(v.leadUserId))
        ctx.addIssue({ code: 'custom', path: ['leadUserId'], message: 'A designer cannot be their own lead' });
    if (v.role !== 'REQUEST_CC' && (v.regionId || v.requesterUserId))
        ctx.addIssue({ code: 'custom', path: ['regionId'], message: 'Only a CC entry can be limited to a region or requester' });
});
// ---------------------------------------------------------------------------
// Audit / timeline vocabulary
// ---------------------------------------------------------------------------
/** `admin_audit_log.entity_type` for every Design Workflow request event. */
export const DESIGN_REQUEST_AUDIT_ENTITY = 'design_request';
/** `admin_audit_log.entity_type` for configuration changes. */
export const DESIGN_CONFIG_AUDIT_ENTITY = 'design_workflow_config';
export const DESIGN_AUDIT_EVENT_LABELS = {
    DESIGN_REQUEST_CREATED: 'Request created',
    DESIGN_REQUEST_UPDATED: 'Request edited',
    DESIGN_REQUEST_SUBMITTED: 'Submitted for regional review',
    DESIGN_REQUEST_RESUBMITTED: 'Resubmitted after changes',
    DESIGN_REGIONAL_APPROVED: 'Regional review approved',
    DESIGN_REGIONAL_CHANGES_REQUESTED: 'Regional coordinator requested changes',
    DESIGN_CONTENT_VERSION_CREATED: 'Content version added',
    DESIGN_CONTENT_APPROVED: 'Content approved',
    DESIGN_CONTENT_CLARIFICATION_REQUESTED: 'Content team requested clarification',
    DESIGN_LEAD_ASSIGNED: 'Design lead assigned',
    DESIGNER_ASSIGNED: 'Designer assigned',
    DESIGN_VERSION_UPLOADED: 'Design version uploaded',
    DESIGN_SUBMITTED: 'Design submitted for lead review',
    DESIGN_APPROVED_BY_LEAD: 'Design approved by lead',
    DESIGN_CHANGES_REQUESTED: 'Design lead requested changes',
    DESIGN_FINAL_CONTENT_APPROVED: 'Final content approved',
    DESIGN_FINAL_CONTENT_CHANGES_REQUESTED: 'Final content review requested a correction',
    DESIGN_APPROVAL_ROUND_STARTED: 'Sent for final approval',
    DESIGN_FINAL_APPROVED: 'Final approver approved',
    DESIGN_FINAL_REJECTED: 'Final approver rejected',
    DESIGN_FINAL_APPROVALS_COMPLETE: 'All final approvals received',
    DESIGN_SENT_TO_BUSINESS: 'Shared with business for confirmation',
    DESIGN_BUSINESS_CONFIRMED: 'Business confirmed for release',
    DESIGN_BUSINESS_CHANGES_REQUESTED: 'Business requested further changes',
    DESIGN_RELEASED: 'Released',
    DESIGN_REQUEST_CANCELLED: 'Request cancelled',
    DESIGN_ETA_SET: 'ETA set',
    DESIGN_REGIONAL_CONTENT_APPROVED: 'Coordinator approved the content for design',
    DESIGN_REGIONAL_CONTENT_RETURNED: 'Coordinator returned the content to the content team',
    DESIGN_SELF_ASSIGNED: 'Design lead took the design work',
    DESIGN_REGIONAL_DESIGN_FORWARDED: 'Coordinator forwarded the design to content QC',
    DESIGN_REGIONAL_DESIGN_RETURNED: 'Coordinator returned the design to the designer',
    DESIGN_CHANGES_ROUTED: 'Coordinator routed the changes',
    DESIGN_SHARED_FOR_FEEDBACK: 'Shared with business for feedback',
    DESIGN_BUSINESS_ACCEPTED_FOR_APPROVAL: 'Business accepted it for final approval',
    DESIGN_BUSINESS_FEEDBACK_CHANGES: 'Business requested changes (feedback)',
    DESIGN_APPROVAL_POLICY_CHANGED: 'Final approval policy changed',
    DESIGN_COMMENT_ADDED: 'Comment added',
    DESIGN_ATTACHMENT_ADDED: 'File attached',
    DESIGN_ATTACHMENT_REMOVED: 'File removed',
};
// ---------------------------------------------------------------------------
// Notifications (outbox -> worker)
// ---------------------------------------------------------------------------
/**
 * What a Design Workflow outbox event means for notifications. The worker
 * resolves recipients from the request and configuration AT SEND TIME (the
 * payload carries ids only) and never notifies the actor of their own action.
 */
export const DESIGN_NOTIFY_EVENTS = [
    /** First submission: email to the regional coordinator with the REQUEST_CC people on CC. */
    'DESIGN_REQUEST_SUBMITTED',
    'DESIGN_REGIONAL_REVIEW_REQUIRED',
    'DESIGN_RETURNED_TO_REQUESTER',
    'DESIGN_CONTENT_REVIEW_REQUIRED',
    'DESIGN_ASSIGNMENT_REQUIRED',
    'DESIGN_LEAD_ASSIGNED',
    'DESIGN_DESIGNER_ASSIGNED',
    'DESIGN_SUBMITTED_FOR_REVIEW',
    'DESIGN_CHANGES_REQUESTED',
    'DESIGN_FINAL_CONTENT_REVIEW_REQUIRED',
    'DESIGN_FINAL_APPROVAL_REQUIRED',
    'DESIGN_FINAL_APPROVALS_COMPLETE',
    'DESIGN_BUSINESS_CONFIRMATION_REQUIRED',
    'DESIGN_FURTHER_CHANGES_REQUESTED',
    'DESIGN_CONFIRMED_FOR_RELEASE',
    'DESIGN_RELEASED',
    'DESIGN_CANCELLED',
    'DESIGN_ETA_CHANGED',
    'DESIGN_REGIONAL_CONTENT_REVIEW_REQUIRED',
    'DESIGN_REGIONAL_DESIGN_REVIEW_REQUIRED',
    'DESIGN_CHANGE_ROUTING_REQUIRED',
    'DESIGN_READY_FOR_FEEDBACK',
    'DESIGN_BUSINESS_FEEDBACK_REQUIRED',
];
/** Short action line used in notification titles and email subjects. */
export const DESIGN_NOTIFY_EVENT_TEXT = {
    DESIGN_REQUEST_SUBMITTED: { title: 'New design request for regional review', action: 'Regional coordinator: review the request and approve it or ask for changes. CC: for your information.' },
    DESIGN_REGIONAL_REVIEW_REQUIRED: { title: 'Regional review required', action: 'Review the request and approve it or ask for changes.' },
    DESIGN_RETURNED_TO_REQUESTER: { title: 'Changes requested on your design request', action: 'Update the request and resubmit it.' },
    DESIGN_CONTENT_REVIEW_REQUIRED: { title: 'Content review required', action: 'Review and correct the content, then approve it.' },
    DESIGN_ASSIGNMENT_REQUIRED: { title: 'Design assignment required', action: 'Assign a design team lead.' },
    DESIGN_LEAD_ASSIGNED: { title: 'Design request assigned to you', action: 'Assign a designer.' },
    DESIGN_DESIGNER_ASSIGNED: { title: 'Design work assigned to you', action: 'Create the design and submit it for review.' },
    DESIGN_SUBMITTED_FOR_REVIEW: { title: 'Design submitted for your review', action: 'Review the design and approve it or request changes.' },
    DESIGN_CHANGES_REQUESTED: { title: 'Design changes requested', action: 'Revise the design and resubmit it.' },
    DESIGN_FINAL_CONTENT_REVIEW_REQUIRED: { title: 'Final content review required', action: 'Verify the content in the completed design.' },
    DESIGN_FINAL_APPROVAL_REQUIRED: { title: 'Your approval is required', action: 'Review the design and approve or reject it.' },
    DESIGN_FINAL_APPROVALS_COMPLETE: { title: 'All final approvals received', action: 'Share the approved files with the business requester.' },
    DESIGN_BUSINESS_CONFIRMATION_REQUIRED: { title: 'Approved design ready for your confirmation', action: 'Confirm it for release or request further changes.' },
    DESIGN_FURTHER_CHANGES_REQUESTED: { title: 'Further changes requested', action: 'Work on the requested changes.' },
    DESIGN_CONFIRMED_FOR_RELEASE: { title: 'Design confirmed for release', action: 'Release the approved files.' },
    DESIGN_RELEASED: { title: 'Design released', action: 'No action needed.' },
    DESIGN_CANCELLED: { title: 'Design request cancelled', action: 'No action needed.' },
    DESIGN_ETA_CHANGED: { title: 'ETA updated on a design request', action: 'Note the new due date.' },
    DESIGN_REGIONAL_CONTENT_REVIEW_REQUIRED: { title: 'Content ready for your check', action: 'Approve the content for design or return it to the content team.' },
    DESIGN_REGIONAL_DESIGN_REVIEW_REQUIRED: { title: 'Design ready for your check', action: 'Forward the design to content QC or return it to the designer.' },
    DESIGN_CHANGE_ROUTING_REQUIRED: { title: 'Changes to route', action: 'Decide whether the content team, the designer or both make the change.' },
    DESIGN_READY_FOR_FEEDBACK: { title: 'Design ready for business feedback', action: 'Share the design with the business team for feedback.' },
    DESIGN_BUSINESS_FEEDBACK_REQUIRED: { title: 'Design ready for your feedback', action: 'Accept it for final approval or request changes.' },
};
// ---------------------------------------------------------------------------
// Pure helpers
// ---------------------------------------------------------------------------
/**
 * True when the approval round is complete under `policy`. Only the current
 * round's records are passed in. A round with no required approver can never
 * complete (the API refuses to start one).
 */
export function isApprovalRoundComplete(policy, approvals) {
    switch (policy) {
        case 'ALL_REQUIRED': {
            const required = approvals.filter((a) => a.isRequired);
            return required.length > 0 && required.every((a) => a.status === 'APPROVED');
        }
        case 'ANY_ONE':
            return approvals.some((a) => a.status === 'APPROVED');
        default: {
            const unknown = policy;
            throw new Error(`Unknown approval policy ${String(unknown)}`);
        }
    }
}
/** True when a round snapshotted under `policy` can be opened with these approvers. */
export function canOpenApprovalRound(policy, approvers) {
    return policy === 'ANY_ONE' ? approvers.length > 0 : approvers.some((a) => a.isRequired);
}
/** Where a change request goes back to. BOTH starts with content (the design is then redone from the corrected copy). */
export function returnStatusForChangeArea(area) {
    return area === 'DESIGN' ? 'DESIGN_IN_PROGRESS' : 'CONTENT_REVIEW';
}
/** The date the request is due: the coordinator's ETA when set, else the requested completion date. */
export function effectiveDueDate(dueDate, etaDate) {
    return etaDate ?? dueDate;
}
/** Overdue = past its (effective) due date and still open. `today` is a YYYY-MM-DD string in the app time zone. */
export function isDesignRequestOverdue(status, dueDate, today) {
    if (!dueDate)
        return false;
    if (status === 'DRAFT' || status === 'RELEASED' || status === 'CANCELLED')
        return false;
    return dueDate < today;
}
//# sourceMappingURL=design-workflow.js.map